 import { useState } from "react";
 import {
   Collapsible,
   CollapsibleContent,
   CollapsibleTrigger,
 } from "@/components/ui/collapsible";
 import { ChevronDown, Calculator, TrendingUp } from "lucide-react";
 import { Input } from "@/components/ui/input";
 import { Label } from "@/components/ui/label";
 import { formatCurrency, formatPercent } from "@/lib/pricing-calculations";
 import { cn } from "@/lib/utils";
 
 interface PricingCalculatorCardProps {
   targetMarginPercent: number;
   onTargetMarginChange: (value: number) => void;
   currentPrice: number;
   suggestedPrice: number;
   realMarginPercent: number;
   grossMargin: number;
   contributionMargin: number;
 }
 
 export function PricingCalculatorCard({
   targetMarginPercent,
   onTargetMarginChange,
   currentPrice,
   suggestedPrice,
   realMarginPercent,
   grossMargin,
   contributionMargin,
 }: PricingCalculatorCardProps) {
   const [isOpen, setIsOpen] = useState(true);
 
   const handleMarginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
     const value = parseFloat(e.target.value);
     if (!isNaN(value) && value >= 0 && value <= 99) {
       onTargetMarginChange(value);
     }
   };
 
   const priceDifference = currentPrice - suggestedPrice;
   const isPriceAboveSuggested = priceDifference >= 0;
 
   return (
     <Collapsible open={isOpen} onOpenChange={setIsOpen}>
       <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
         <div className="flex items-center gap-2">
           <Calculator className="h-4 w-4 text-muted-foreground" />
           <span className="font-medium text-sm">Precificação</span>
         </div>
         <ChevronDown 
           className={cn(
             "h-4 w-4 text-muted-foreground transition-transform",
             isOpen && "rotate-180"
           )} 
         />
       </CollapsibleTrigger>
       <CollapsibleContent>
         <div className="p-3 pt-2 space-y-4">
           {/* Margem Desejada */}
           <div className="flex items-center gap-3">
             <Label htmlFor="target-margin" className="text-sm whitespace-nowrap">
               Margem Desejada:
             </Label>
             <div className="relative w-24">
               <Input
                 id="target-margin"
                 type="number"
                 min="0"
                 max="99"
                 step="1"
                 value={targetMarginPercent}
                 onChange={handleMarginChange}
                 className="pr-6 text-center"
               />
               <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                 %
               </span>
             </div>
           </div>
 
           {/* Preço Atual vs Sugerido */}
           <div className="grid grid-cols-2 gap-3">
             <div className="p-3 bg-background rounded-lg border">
               <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                 Preço Atual
               </p>
               <p className="font-bold text-lg text-primary mt-1">
                 {formatCurrency(currentPrice)}
               </p>
               <p className="text-xs text-muted-foreground mt-0.5">
                 Margem Real: <span className={cn(
                   "font-medium",
                   realMarginPercent >= targetMarginPercent ? "text-green-600" : "text-yellow-600"
                 )}>{formatPercent(realMarginPercent)}</span>
               </p>
             </div>
             <div className="p-3 bg-background rounded-lg border">
               <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                 Preço Sugerido
               </p>
               <p className="font-bold text-lg text-foreground mt-1">
                 {formatCurrency(suggestedPrice)}
               </p>
               <p className="text-xs text-muted-foreground mt-0.5">
                 Para margem de {formatPercent(targetMarginPercent, 0)}
               </p>
             </div>
           </div>
 
           {/* Indicador de diferença */}
           {currentPrice > 0 && (
             <div className={cn(
               "flex items-center gap-2 p-2 rounded-lg text-xs",
               isPriceAboveSuggested ? "bg-green-100 dark:bg-green-900/30" : "bg-yellow-100 dark:bg-yellow-900/30"
             )}>
               <TrendingUp className={cn(
                 "h-3.5 w-3.5",
                 isPriceAboveSuggested ? "text-green-600" : "text-yellow-600"
               )} />
               <span className={isPriceAboveSuggested ? "text-green-700 dark:text-green-400" : "text-yellow-700 dark:text-yellow-400"}>
                 {isPriceAboveSuggested 
                   ? `Preço atual está ${formatCurrency(Math.abs(priceDifference))} acima do sugerido`
                   : `Preço atual está ${formatCurrency(Math.abs(priceDifference))} abaixo do sugerido`
                 }
               </span>
             </div>
           )}
 
           {/* Margens */}
           <div className="flex gap-4 pt-2 border-t">
             <div className="flex-1">
               <p className="text-xs text-muted-foreground">Margem Bruta</p>
               <p className="font-semibold text-sm text-foreground">
                 {formatCurrency(grossMargin)}
               </p>
             </div>
             <div className="flex-1">
               <p className="text-xs text-muted-foreground">Margem de Contribuição</p>
               <p className={cn(
                 "font-semibold text-sm",
                 contributionMargin >= 0 ? "text-green-600" : "text-destructive"
               )}>
                 {formatCurrency(contributionMargin)}
               </p>
             </div>
           </div>
         </div>
       </CollapsibleContent>
     </Collapsible>
   );
 }